<script lang="ts">
  import { router } from '@inertiajs/svelte';
  import type {
    View,
    Scale,
    Range,
    DisplayFilter,
    FavoriteView,
    FavoritePageType,
    CalendarLayout,
  } from '../lib/types';
  import { getFavoritePageType, CALENDAR_LAYOUT_LABELS } from '../lib/types';
  import { favoriteViews, addFavorite, removeFavorite } from '../lib/stores/favorites';

  interface Props {
    view?: View;
    scale?: Scale;
    range?: Range;
    display?: DisplayFilter | null;
    // Calendar page support
    pageType?: FavoritePageType;
    layout?: CalendarLayout;
  }

  let { view, scale, range, display, pageType = 'timeline', layout }: Props = $props();

  let showPopover = $state(false);
  let favName = $state('');
  let favIcon = $state('');
  let isSaving = $state(false);
  let saveError = $state('');
  let showRemoveConfirm = $state(false);
  let isRemoving = $state(false);

  const scaleLabels: Record<Scale, string> = { month: 'Month', week: 'Week', year: 'Year' };
  const viewLabels: Record<View, string> = {
    focus: 'Focus',
    rolling: 'Rolling',
    days: 'Days',
    table: 'Table',
    timeline: 'Timeline',
    gantt: 'Gantt',
    'grid-6x2': 'Grid 6×2',
    'grid-quarters': 'Quarters',
    tasks: 'Tasks',
  };
  const rangeLabels: Record<string, string> = {
    '1-month': '1 Month',
    quarter: 'Quarter',
    '3-months': '3 Months',
    '6-months': '6 Months',
    year: 'Year',
    '1-week': '1 Week',
    month: 'Month',
    '5': '5 Weeks',
    '7': '7 Weeks',
    full: 'Full Year',
    half: 'Half Year',
  };

  // Find if current config matches an existing favorite
  // Normalize null/undefined for comparison (backend strips nil values via compact_blank)
  function eq(a: any, b: any): boolean {
    return (a ?? null) === (b ?? null);
  }

  let matchingFavorite = $derived.by(() => {
    const favs = currentFavorites;
    if (pageType === 'calendar') {
      return (
        favs.find((f) => getFavoritePageType(f) === 'calendar' && eq(f.layout, layout)) || null
      );
    }
    return (
      favs.find(
        (f) =>
          getFavoritePageType(f) === 'timeline' &&
          f.view === view &&
          f.scale === scale &&
          eq(f.range, range) &&
          eq(f.display, display),
      ) || null
    );
  });

  let isFavorited = $derived(!!matchingFavorite);

  // Subscribe to store
  let currentFavorites = $derived($favoriteViews);

  function generateDefaultName(): string {
    if (pageType === 'calendar') {
      const parts = ['Calendar'];
      if (layout) parts.push(CALENDAR_LAYOUT_LABELS[layout] || layout);
      return parts.join(' · ');
    }
    const parts = [scaleLabels[scale!], viewLabels[view!]];
    if (range) parts.push(String(range));
    return parts.join(' · ');
  }

  function togglePopover() {
    if (isFavorited) {
      // Show remove confirmation
      showRemoveConfirm = !showRemoveConfirm;
      showPopover = false;
      return;
    }
    // Open save popover
    favName = generateDefaultName();
    favIcon = '';
    saveError = '';
    showRemoveConfirm = false;
    showPopover = !showPopover;
  }

  async function handleRemove() {
    if (!matchingFavorite || isRemoving) return;
    isRemoving = true;
    try {
      await removeFavorite(matchingFavorite.id);
      showRemoveConfirm = false;
    } catch (e: any) {
      console.error('Failed to remove favorite:', e);
    } finally {
      isRemoving = false;
    }
  }

  // Check if this exact config is already saved (using normalized comparison)
  let alreadyExists = $derived.by(() => {
    if (pageType === 'calendar') {
      return currentFavorites.some(
        (f) => getFavoritePageType(f) === 'calendar' && eq(f.layout, layout),
      );
    }
    return currentFavorites.some(
      (f) =>
        getFavoritePageType(f) === 'timeline' &&
        f.view === view &&
        f.scale === scale &&
        eq(f.range, range) &&
        eq(f.display, display),
    );
  });

  async function handleSave() {
    if (!favName.trim() || isSaving) return;
    isSaving = true;
    saveError = '';
    try {
      if (pageType === 'calendar') {
        await addFavorite({
          name: favName.trim(),
          icon: favIcon || undefined,
          page_type: 'calendar',
          layout: layout,
        });
      } else {
        await addFavorite({
          name: favName.trim(),
          icon: favIcon || undefined,
          view: view!,
          scale: scale!,
          range: range,
          display: display,
        });
      }
      showPopover = false;
    } catch (e: any) {
      console.error('Failed to save favorite:', e);
      saveError = e?.message || 'Failed to save. Please try again.';
    } finally {
      isSaving = false;
    }
  }

  function handleKeydown(e: KeyboardEvent) {
    if (e.key === 'Enter') handleSave();
    if (e.key === 'Escape') showPopover = false;
  }

  // Close popover on click outside
  function handleClickOutside(e: MouseEvent) {
    const target = e.target as HTMLElement;
    if (!target.closest('.favorite-star-container')) {
      showPopover = false;
      showRemoveConfirm = false;
    }
  }

  let isAnyPopoverOpen = $derived(showPopover || showRemoveConfirm);

  // eslint-disable-next-line momentium/no-unguarded-effect -- document-level event listener with conditional setup/teardown and cleanup
  $effect(() => {
    if (isAnyPopoverOpen) {
      document.addEventListener('click', handleClickOutside, true);
      return () => document.removeEventListener('click', handleClickOutside, true);
    }
  });

  const commonIcons = ['⭐', '🏃', '📋', '🗓️', '🎯', '📊', '🔥', '💼', '🏠', '🌟', '📌', '🚀'];
</script>

<div class="favorite-star-container">
  <button
    class="star-btn"
    class:active={isFavorited}
    onclick={togglePopover}
    title={isFavorited ? 'Remove from favorites' : 'Save as favorite'}
  >
    {isFavorited ? '★' : '☆'}
  </button>

  {#if showPopover}
    <div class="save-popover">
      {#if alreadyExists}
        <div class="popover-title">Already Favorited</div>
        <div class="popover-preview">
          {#if pageType === 'calendar'}
            <span class="preview-badge">Page: Calendar</span>
            {#if layout}
              <span class="preview-badge">Layout: {CALENDAR_LAYOUT_LABELS[layout] || layout}</span>
            {/if}
          {:else}
            <span class="preview-badge">View: {viewLabels[view!]}</span>
            <span class="preview-badge">Scale: {scaleLabels[scale!]}</span>
            {#if range}
              <span class="preview-badge">Range: {rangeLabels[range as string] || range}</span>
            {/if}
            {#if display && display !== 'both'}
              <span class="preview-badge">Show: {display}</span>
            {/if}
          {/if}
        </div>
        <p class="popover-exists-msg">This view configuration is already saved as a favorite.</p>
        <div class="popover-actions">
          <button class="popover-cancel" onclick={() => (showPopover = false)}>Close</button>
          <button class="popover-manage-link" onclick={() => router.get('/settings')}
            >Edit favorites</button
          >
        </div>
      {:else}
        <div class="popover-title">Save as Favorite</div>
        <div class="popover-preview">
          {#if pageType === 'calendar'}
            <span class="preview-badge">Page: Calendar</span>
            {#if layout}
              <span class="preview-badge">Layout: {CALENDAR_LAYOUT_LABELS[layout] || layout}</span>
            {/if}
          {:else}
            <span class="preview-badge">View: {viewLabels[view!]}</span>
            <span class="preview-badge">Scale: {scaleLabels[scale!]}</span>
            {#if range}
              <span class="preview-badge">Range: {rangeLabels[range as string] || range}</span>
            {/if}
            {#if display && display !== 'both'}
              <span class="preview-badge">Show: {display}</span>
            {/if}
          {/if}
        </div>

        <div class="popover-field">
          <label class="popover-label" for="fav-name">Name</label>
          <input
            autocomplete="off"
            id="fav-name"
            class="popover-input"
            type="text"
            bind:value={favName}
            onkeydown={handleKeydown}
            maxlength="50"
            placeholder="My favorite view..."
          />
        </div>

        <div class="popover-field">
          <span class="popover-label">Icon (optional)</span>
          <div class="icon-picker" role="group" aria-label="Icon picker">
            {#each commonIcons as icon}
              <button
                class="icon-option"
                class:selected={favIcon === icon}
                onclick={() => (favIcon = favIcon === icon ? '' : icon)}>{icon}</button
              >
            {/each}
          </div>
        </div>

        {#if saveError}
          <div class="popover-error">{saveError}</div>
        {/if}

        <div class="popover-actions">
          <button class="popover-cancel" onclick={() => (showPopover = false)}>Cancel</button>
          <button class="popover-save" onclick={handleSave} disabled={!favName.trim() || isSaving}
            >{isSaving ? 'Saving...' : 'Save'}</button
          >
        </div>
      {/if}
    </div>
  {/if}

  {#if showRemoveConfirm && matchingFavorite}
    <div class="save-popover">
      <div class="popover-title">Remove Favorite</div>
      <p class="popover-remove-name">{matchingFavorite.icon || '⭐'} {matchingFavorite.name}</p>
      <div class="popover-preview">
        {#if pageType === 'calendar'}
          <span class="preview-badge">Page: Calendar</span>
          {#if layout}
            <span class="preview-badge">Layout: {CALENDAR_LAYOUT_LABELS[layout] || layout}</span>
          {/if}
        {:else}
          <span class="preview-badge">View: {viewLabels[view!]}</span>
          <span class="preview-badge">Scale: {scaleLabels[scale!]}</span>
          {#if range}
            <span class="preview-badge">Range: {rangeLabels[range as string] || range}</span>
          {/if}
          {#if display && display !== 'both'}
            <span class="preview-badge">Show: {display}</span>
          {/if}
        {/if}
      </div>
      <div class="popover-actions">
        <button class="popover-cancel" onclick={() => (showRemoveConfirm = false)}>Cancel</button>
        <button class="popover-remove" onclick={handleRemove} disabled={isRemoving}
          >{isRemoving ? 'Removing...' : 'Remove'}</button
        >
      </div>
    </div>
  {/if}
</div>

<style>
  .favorite-star-container {
    position: relative;
    display: flex;
    align-items: center;
  }

  .star-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1.5rem;
    height: 1.5rem;
    padding: 0;
    font-size: 0.875rem;
    color: #9ca3af;
    background: transparent;
    border: 1px solid #d1d5db;
    border-radius: 0.1875rem;
    cursor: pointer;
    transition: all 0.15s;
    line-height: 1;
  }

  .star-btn:hover {
    color: #f59e0b;
    border-color: #f59e0b;
    background: #fffbeb;
  }

  .star-btn.active {
    color: #f59e0b;
    border-color: #f59e0b;
    background: #fffbeb;
  }

  .save-popover {
    position: absolute;
    top: calc(100% + 0.375rem);
    right: 0;
    width: 16rem;
    padding: 0.75rem;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 0.5rem;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
    z-index: 60;
    animation: popoverFadeIn 0.15s ease;
  }

  .popover-title {
    font-size: 0.75rem;
    font-weight: 600;
    color: #111827;
    margin-bottom: 0.375rem;
  }

  .popover-preview {
    display: flex;
    flex-wrap: wrap;
    gap: 0.1875rem;
    margin-bottom: 0.625rem;
  }

  .preview-badge {
    font-size: 0.5625rem;
    color: #6b7280;
    background: #f3f4f6;
    border: 1px solid #e5e7eb;
    border-radius: 0.1875rem;
    padding: 0.0625rem 0.3125rem;
    line-height: 1.5;
    white-space: nowrap;
  }

  .popover-field {
    margin-bottom: 0.5rem;
  }

  .popover-label {
    display: block;
    font-size: 0.6875rem;
    font-weight: 500;
    color: #374151;
    margin-bottom: 0.25rem;
  }

  .popover-input {
    width: 100%;
    padding: 0.3125rem 0.5rem;
    font-size: 0.6875rem;
    border: 1px solid #d1d5db;
    border-radius: 0.25rem;
    outline: none;
    transition: border-color 0.15s;
    box-sizing: border-box;
  }

  .popover-input:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
  }

  .icon-picker {
    display: flex;
    flex-wrap: wrap;
    gap: 0.1875rem;
  }

  .icon-option {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1.5rem;
    height: 1.5rem;
    font-size: 0.75rem;
    background: transparent;
    border: 1px solid #e5e7eb;
    border-radius: 0.25rem;
    cursor: pointer;
    transition: all 0.1s;
    padding: 0;
  }

  .icon-option:hover {
    background: #f3f4f6;
    border-color: #d1d5db;
  }

  .icon-option.selected {
    background: #eff6ff;
    border-color: #3b82f6;
  }

  .popover-remove-name {
    font-size: 0.75rem;
    font-weight: 500;
    color: #1f2937;
    margin-bottom: 0.375rem;
  }

  .popover-remove {
    padding: 0.25rem 0.625rem;
    font-size: 0.6875rem;
    font-weight: 500;
    color: white;
    background: #dc2626;
    border: none;
    border-radius: 0.25rem;
    cursor: pointer;
  }

  .popover-remove:hover:not(:disabled) {
    background: #b91c1c;
  }

  .popover-remove:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .popover-exists-msg {
    font-size: 0.6875rem;
    color: #6b7280;
    line-height: 1.4;
    margin-bottom: 0.5rem;
  }

  .popover-manage-link {
    padding: 0.25rem 0.625rem;
    font-size: 0.6875rem;
    font-weight: 500;
    color: #3b82f6;
    background: #eff6ff;
    border: 1px solid #bfdbfe;
    border-radius: 0.25rem;
    cursor: pointer;
    transition: all 0.15s;
  }

  .popover-manage-link:hover {
    background: #dbeafe;
    border-color: #93c5fd;
  }

  .popover-error {
    font-size: 0.625rem;
    color: #dc2626;
    background: #fef2f2;
    border: 1px solid #fecaca;
    border-radius: 0.25rem;
    padding: 0.25rem 0.375rem;
    margin-bottom: 0.5rem;
    line-height: 1.4;
    word-break: break-word;
  }

  .popover-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.375rem;
    margin-top: 0.625rem;
  }

  .popover-cancel {
    padding: 0.25rem 0.5rem;
    font-size: 0.6875rem;
    color: #6b7280;
    background: transparent;
    border: 1px solid #d1d5db;
    border-radius: 0.25rem;
    cursor: pointer;
  }

  .popover-cancel:hover {
    background: #f3f4f6;
  }

  .popover-save {
    padding: 0.25rem 0.625rem;
    font-size: 0.6875rem;
    font-weight: 500;
    color: white;
    background: #3b82f6;
    border: none;
    border-radius: 0.25rem;
    cursor: pointer;
  }

  .popover-save:hover:not(:disabled) {
    background: #2563eb;
  }

  .popover-save:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @keyframes popoverFadeIn {
    from {
      opacity: 0;
      transform: translateY(-4px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
</style>
